// src/pages/Login.js
import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { auth } from "../firebaseConfig";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  getRedirectResult,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";

export default function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isRegister, setIsRegister] = useState(false);

  // Detect if running inside native mobile app
  const isNative = Capacitor?.isNativePlatform?.() ?? false;

  const goToJournal = () => navigate("/journal");

  React.useEffect(() => {
    const checkRedirect = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) goToJournal();
      } catch (err) {
        console.log("Redirect sign-in error:", err);
      }
    };
    checkRedirect();
  }, []);

  const handleGoogleLogin = async () => {
    setError("");

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });

    try {
      if (isNative) {
        // No Google login on native anymore
        return;
      }

      await signInWithPopup(auth, provider);
      goToJournal();
    } catch (err) {
      console.error("Google sign-in error:", err);
      setError("Google sign in failed. Please try again.");
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError("");

    try {
      if (isRegister) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      goToJournal();
    } catch (err) {
      console.error("Email auth error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <Helmet>
        <title>Login – ReflectRise</title>
      </Helmet>

      <div
        className="login-page"
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "1rem",
          position: "relative",
          overflow: "hidden",
          background:
            "linear-gradient(135deg, #0d0f1e 0%, #15172b 35%, #0b0b13 100%)",
        }}
      >
        {/* Background Glows */}
        <div
          style={{
            position: "absolute",
            top: "-40%",
            left: "-20%",
            width: "160%",
            height: "160%",
            background:
              "radial-gradient(circle at 30% 30%, rgba(132,0,255,0.35), transparent 60%), radial-gradient(circle at 70% 70%, rgba(0,119,255,0.35), transparent 60%)",
            filter: "blur(130px)",
            animation: "moveGlow 14s ease-in-out infinite alternate",
            zIndex: 0,
          }}
        />

        {/* Card container */}
        <div
          className="login-container"
          style={{
            width: "100%",
            maxWidth: "420px",
            background: "rgba(20, 20, 35, 0.78)",
            backdropFilter: "blur(14px)",
            borderRadius: "18px",
            padding: "2.3rem",
            boxShadow: "0 10px 30px rgba(0,0,0,0.50)",
            color: "var(--text)",
            position: "relative",
            zIndex: 2,
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "1.3rem" }}>
            <img
              src="/logo192.png"
              alt="ReflectRise Logo"
              style={{
                width: "64px",
                marginBottom: "0.9rem",
                borderRadius: "12px",
              }}
            />

            <h2 style={{ marginBottom: "0.4rem", color: "var(--primary)" }}>
              Welcome to ReflectRise
            </h2>

            <p style={{ fontSize: "0.9rem", color: "var(--muted)" }}>
              {isRegister
                ? "Create an account to begin your journey."
                : "Sign in to start journaling and tracking your growth."}
            </p>
          </div>

          {/* Email Login */}
          <form onSubmit={handleEmailAuth}>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={inputStyle}
            />

            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={inputStyle}
            />

            {error && (
              <p
                style={{
                  color: "#ff6b6b",
                  marginTop: "5px",
                  fontSize: "0.85rem",
                }}
              >
                {error}
              </p>
            )}

            <button type="submit" style={primaryBtnStyle}>
              {isRegister ? "Create Account" : "Sign In"}
            </button>
          </form>

          {/* Divider */}
          <div
            style={{
              margin: "1rem 0",
              textAlign: "center",
              fontSize: "0.85rem",
              color: "var(--muted)",
            }}
          >
            {!isNative && "or"}
          </div>

          {/* GOOGLE BUTTON — HIDDEN ON APP */}
          {!isNative && (
            <button onClick={handleGoogleLogin} style={googleBtnStyle}>
              <img
                src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg"
                alt="Google Logo"
                style={{ width: "20px", marginRight: "8px" }}
              />
              Sign in with Google
            </button>
          )}

          {/* Register switch */}
          <p
            style={{
              marginTop: "1.3rem",
              textAlign: "center",
              fontSize: "0.9rem",
              color: "var(--text)",
            }}
          >
            {isRegister ? "Already have an account?" : "New here?"}{" "}
            <span
              style={{ color: "var(--primary)", cursor: "pointer" }}
              onClick={() => setIsRegister(!isRegister)}
            >
              {isRegister ? "Sign In" : "Create one"}
            </span>
          </p>
        </div>

        <style>
          {`
            @keyframes moveGlow {
              0% { transform: translate(-10%, -10%) scale(1); }
              100% { transform: translate(10%, 10%) scale(1.25); }
            }
          `}
        </style>
      </div>
    </>
  );
}

const inputStyle = {
  width: "100%",
  padding: "0.75rem 1rem",
  marginBottom: "0.75rem",
  borderRadius: "10px",
  border: "1px solid rgba(255,255,255,0.15)",
  background: "rgba(255,255,255,0.06)",
  color: "var(--text)",
  fontSize: "0.95rem",
};

const primaryBtnStyle = {
  width: "100%",
  padding: "0.75rem",
  borderRadius: "10px",
  background: "var(--primary)",
  color: "#fff",
  fontWeight: "600",
  border: "none",
  cursor: "pointer",
  marginTop: "0.4rem",
};

const googleBtnStyle = {
  width: "100%",
  padding: "0.75rem",
  borderRadius: "10px",
  background: "rgba(255,255,255,0.10)",
  color: "var(--text)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid rgba(255,255,255,0.15)",
  cursor: "pointer",
};
